import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger, useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  InlineMessage,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import useResponsive from '../../../../hooks/use-responsive';
import { getPredefinedComments } from '../../../../redux/certlist/core/action';
import {
  callDiscontinueAPI,
  setCertsActionIsLoading,
  setCertsListActionStatus,
} from '../../../../redux/certlist/lineaction/action';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import CertsModalCommentBox from '../../../CertsModalCommentBox';
import {
  ACT_DISCONTINUE,
  CERTS_LIST_PREDEFINED_COMMENTS_FOR_ACTION,
} from '../../constant';

import invertedMessages from './messages';

import './CertsListDiscontinue.css';

function CertsListDiscontinue({
  isOpen,
  setIsOpen,
  certKey,
  certificationName,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [commentDescription, setCommentDescription] = useState('');
  const [predefComments, setPredefComments] = useState([]);
  const [showAlert, setShowAlert] = useState(false);

  const {
    core: { predefinedComments } = {},
    lineAction: { certsActionIsLoading, certsActionStatus } = {},
  } = useSelector((state) => state?.certifications?.list);

  const { messages } = useSelector((state) => state?.user);
  const l = useInvertedLocalize(invertedMessages);

  const closeButtonRef = useRef(null);
  const { isMobileXlargeWidth } = useResponsive();
  const { isScrolled } = useDetectScroll();

  const title =
    messages.CERTS_LIST_ACTION_DISCONTINUE_TITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_DISCONTINUE_TITLE'));
  const subtitle =
    messages.CERTS_LIST_ACTION_DISCONTINUE_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_DISCONTINUE_SUBTITLE'));
  const headerIcon = () => (
    <Icon
      kind='AlertWarningFilled'
      color='--color-foreground-warning-subtle'
      size='large'
    />
  );

  const isPredefinedCommentsForCertKeyExists = (key) => {
    if (!predefinedComments) return false;

    const certKeyStr = key.toString();

    if (certKeyStr in predefinedComments) {
      const comments = predefinedComments[certKeyStr].CERTLISTCANCEL;

      if (comments?.length) {
        setPredefComments(comments);

        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (isOpen && !isPredefinedCommentsForCertKeyExists(certKey)) {
      dispatch(
        getPredefinedComments(
          CERTS_LIST_PREDEFINED_COMMENTS_FOR_ACTION.CERT_LIST_CANCEL,
          certKey
        )
      );
    }
  }, [isOpen]);

  useEffect(() => {
    isPredefinedCommentsForCertKeyExists(certKey);
  }, [predefinedComments]);

  useEffect(() => {
    if (commentDescription) setShowAlert(false);
  }, [commentDescription, certKey]);

  const handleClose = () => {
    setIsOpen('');
    setShowAlert(false);
    dispatch(setCertsActionIsLoading(false));

    if (certsActionStatus?.status === 'FAILED') {
      dispatch(setCertsListActionStatus(null));
    }
  };

  const isSubmitButtonEnable = () => {
    const isCommentLengthGreaterThan3 =
      commentDescription?.replace(/[<>]/g, '')?.trim()?.length >= 3;
    const hasAngleBrackets = /<|>/.test(commentDescription);

    return isCommentLengthGreaterThan3 && !hasAngleBrackets;
  };

  const handleSumbit = async () => {
    if (!commentDescription || !isSubmitButtonEnable()) {
      setShowAlert(true);

      return;
    }

    const payload = {
      certkey: certKey,
      comments: commentDescription,
      certificationName,
    };

    try {
      const result = await dispatch(callDiscontinueAPI(payload));

      if (result.success) {
        handleClose();
      } else {
        Logger.error('Discontinue API call failed');
      }
    } catch (error) {
      Logger.error('Discontinue API call error:', error);
    }
  };

  const isAPIfailed =
    certsActionStatus?.status === 'FAILED' &&
    certsActionStatus?.action === ACT_DISCONTINUE;

  const APIFailedMessage =
    messages.CERTS_LIST_ACTION_API_FAILURE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_API_FAILURE'));

  const getDiscontinueContentBody = () => (
    <>
      <InlineMessage
        kind='background'
        text={APIFailedMessage}
        colorTheme='critical'
        size='medium'
        isVisible={isAPIfailed}
        leftIcon={<Icon kind='AlertCriticalFilled' />}
        className='CertsListActionAPIFailure'
      />

      <CertsModalCommentBox
        defaultComments={predefComments}
        onChange={setCommentDescription}
        showAlert={showAlert}
        allowEditPredefinedComments={
          predefinedComments[certKey.toString()]?.allowEditPredefinedComments
        }
      />
    </>
  );

  const getDiscontinueFooter = () => (
    <FooterActionBar
      className='CertsListDiscontine-footer'
      size='large'
      isScrolled={isScrolled}>
      <div className='CertsListDiscontine-actionButtons'>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          isDisabled={false}
          onClick={handleClose}>
          {messages.CERTS_CANCEL ||
            localize(intl, getMessageKey('CERTS_CANCEL'))}
        </Button>
        <Button
          className='CertsListDiscontinue-submitButton'
          type='button'
          kind={isSubmitButtonEnable() ? 'filled' : 'outlined'}
          size='medium'
          onClick={handleSumbit}
          isLoading={certsActionIsLoading}
          loaderText={
            messages.CERTS_LOADING ||
            localize(intl, getMessageKey('CERTS_LOADING'))
          }>
          {messages.CERTS_SUBMIT ||
            localize(intl, getMessageKey('CERTS_SUBMIT'))}
        </Button>
      </div>
    </FooterActionBar>
  );

  if (isMobileXlargeWidth) {
    const getHeaderContentMobile = () => (
      <>
        <div className='CertsListDiscontinue-mobileHeader'>
          {headerIcon()}
          <div className=''>
            <Typography kind='h1' desktop='h1-d' mobile='h1-d'>
              {title}
            </Typography>
            <Typography kind='body3' desktop='body3-d' mobile='body3-d'>
              {subtitle}
            </Typography>
          </div>
        </div>

        <ButtonIcon
          ref={closeButtonRef}
          size='medium'
          kind='ghost'
          className='Example-MobileDrawer-closeButton'
          ariaLabel={l('Close')}
          icon={<Icon kind='Close' />}
          onClick={handleClose}
        />
      </>
    );

    return (
      <MobileDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCloseButtonEnabled
        content={
          <Box className='CertsListDiscontinue-mobileBody'>
            {getDiscontinueContentBody()}
          </Box>
        }
        headerContent={getHeaderContentMobile()}
        footerActionBar={getDiscontinueFooter()}
        className='CertsListDiscontinue-mobileDrawer'
      />
    );
  }

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleClose}
      headerIcon={headerIcon()}
      title={title}
      subtitle={subtitle}
      contentBody={getDiscontinueContentBody()}
      FooterActionBarComp={getDiscontinueFooter()}
      className='CertsListDiscontinue'
    />
  );
}

CertsListDiscontinue.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  certKey: PropTypes.number.isRequired,
  certificationName: PropTypes.string.isRequired,
};

export default CertsListDiscontinue;
